import { SessionCustomer } from '@portal/appLayer/auth';
import { LocaleEnum, LocaleStore } from '@portal/schema/locale';
import {
  BYPASS_KEY,
  createBypassString,
  normalizeBypassData
} from '@portal/shared/utils/statsig/bypass';
import { StatsigUser } from '@statsig/react-bindings';
import Cookies from 'js-cookie';
import { NextRequest, userAgent } from 'next/server';
import { StatsigUser as NodeStatsigUser } from 'statsig-node-lite';

/**
 * Returns the cookie name used by Statsig for the stable ID
 *
 * Currently returns the hardcoded 'statsig-uuid' for backward compatibility,
 * but will eventually return the dynamically generated name used by the Statsig SDK.
 *
 * @returns The name of the cookie used for the stable ID
 */
export function getStatsigCookieName(_sdkKey: string): string {
  // This mimics the StableID._getCookieName implementation
  return 'statsig-uuid'; // For now returning the hardcoded name
  // TODO: Change to return `statsig.stable_id.${getStorageKey(sdkKey)}` once fully migrated
}

/**
 * Gets the stable ID from cookies or generates a new one
 *
 * @param request NextRequest object
 * @param cookieName The name of the cookie to check
 * @returns The stable ID and whether it existed
 */
export function getStableIdFromRequest(request: NextRequest, cookieName: string): string {
  const stableId = request.cookies.get(cookieName)?.value;
  const hasStableId = !!stableId && /^[0-9a-f-]+$/i.test(stableId);

  if (hasStableId) {
    return stableId!;
  }

  return crypto.randomUUID();
}

/**
 * Creates a StatsigUser object for use in client-side code
 *
 * @param customer The authenticated customer (optional)
 * @param store The store code
 * @param additionalProperties Additional properties to include in the user object
 * @returns A StatsigUser object
 */
export function createClientStatsigUser(
  customer?: SessionCustomer | null,
  store?: string,
  additionalProperties: Record<string, unknown> = {}
): StatsigUser {
  const sdkKey = process.env.NEXT_PUBLIC_STATSIG_CLIENT_API_KEY;
  const cookieName = getStatsigCookieName(sdkKey || '');

  // Get the stable ID from cookie
  const stableId = Cookies.get(cookieName);
  // Get bypass from cookie
  const bypassArray = Object.entries(Cookies.get() ?? {}).find(([key]) =>
    key.startsWith(BYPASS_KEY)
  );
  const bypass = createBypassString(normalizeBypassData({ bypassArray }));

  // User properties
  const user: StatsigUser = {
    userID: customer?.id?.toString() || undefined,
    email: customer?.email || undefined,
    custom: {
      ...additionalProperties,
      ...(bypass && { [BYPASS_KEY]: bypass }),
      store
    }
  };

  // Add stable ID if available
  if (stableId) {
    user.customIDs = {
      stableID: stableId
    };
  }

  return user;
}

/**
 * Creates a StatsigUser object for use in Edge Runtime
 *
 * @param request NextRequest object
 * @param path Current path
 * @param detectedFullLocale The detected locale
 * @param stableId The stable ID for the user
 * @param bypass The bypass string if available
 * @returns A StatsigUser object
 */
export function createEdgeStatsigUser(
  request: NextRequest,
  path: string,
  detectedFullLocale: LocaleEnum,
  stableId: string,
  bypass?: string | null,
  countryCode?: string
): NodeStatsigUser {
  const locale = LocaleStore[detectedFullLocale];
  const { device, os } = userAgent(request);

  const isMobile = () => {
    if (device.type === 'mobile') return true;
    if (os.name === 'Android' || os.name === 'iOS') return true;
    return false;
  }

  return {
    country: countryCode || locale,
    custom: {
      store: locale,
      url: request.url,
      path,
      ...(bypass && { [BYPASS_KEY]: bypass }),
      os: os.name,
      isMobile: isMobile(),
    },
    customIDs: {
      stableID: stableId
    }
  };
}
